import React, { useState, useEffect, useContext } from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EnvironmentContext } from '../Context/EnvironmentContext';
import './CreditsWidget.css';

const CreditsWidget = () => {
    const { t } = useTranslation();
    const env = useContext(EnvironmentContext);
    const [prevCredits, setPrevCredits] = useState(env.userTokenCount);
    const [isAnimating, setIsAnimating] = useState(false);
    const [changeAmount, setChangeAmount] = useState(0);
    const [showPulse, setShowPulse] = useState(false);

    const LOW_CREDITS_THRESHOLD = 100;
    const isLow = env.userTokenCount < LOW_CREDITS_THRESHOLD;

    // Detect credit changes
    useEffect(() => {
        if (prevCredits !== env.userTokenCount && prevCredits !== null) {
            const diff = env.userTokenCount - prevCredits;
            setChangeAmount(diff);
            setIsAnimating(true);

            // Show pulse animation
            setShowPulse(true);
            setTimeout(() => setShowPulse(false), 1000);

            // Reset animation
            setTimeout(() => setIsAnimating(false), 2000);
        }
        setPrevCredits(env.userTokenCount);
    }, [env.userTokenCount, prevCredits]);

    const formatCredits = (amount) => {
        if (amount >= 1000000) {
            return `${(amount / 1000000).toFixed(1)}M`;
        }
        if (amount >= 10000) {
            return `${(amount / 1000).toFixed(1)}k`;
        }
        return amount?.toLocaleString() || '0';
    };

    return (
        <Popup
            trigger={
                <Link to="/Buy" className="credits-widget-link">
                    <motion.div 
                        className={`credits-widget ${isLow ? 'low' : ''} ${showPulse ? 'pulse' : ''}`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {/* Icon */}
                        <motion.div 
                            className="credits-icon"
                            animate={isAnimating ? { rotate: [0, 360] } : {}}
                            transition={{ duration: 0.5 }}
                        >
                            <Icon name="star" />
                        </motion.div>

                        {/* Amount */}
                        <motion.span 
                            className="credits-amount"
                            animate={isAnimating ? { scale: [1, 1.2, 1] } : {}}
                        >
                            {formatCredits(env.userTokenCount)}
                        </motion.span>

                        {/* Change indicator */}
                        <AnimatePresence>
                            {isAnimating && changeAmount !== 0 && (
                                <motion.span
                                    className={`credits-change ${changeAmount > 0 ? 'positive' : 'negative'}`}
                                    initial={{ opacity: 0, y: 0 }}
                                    animate={{ opacity: 1, y: -20 }}
                                    exit={{ opacity: 0, y: -30 }}
                                    transition={{ duration: 1.5 }}
                                >
                                    {changeAmount > 0 ? '+' : ''}{changeAmount.toLocaleString()}
                                </motion.span>
                            )}
                        </AnimatePresence>

                        {/* Low indicator */}
                        {isLow && (
                            <motion.div
                                className="low-indicator"
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                            >
                                <Icon name="exclamation" />
                            </motion.div>
                        )}

                        {/* Plus icon for buy */}
                        <div className="buy-indicator">
                            <Icon name="plus" />
                        </div>
                    </motion.div>
                </Link>
            }
            content={
                <div className="credits-popup">
                    <p className="popup-title">{t('Credits')}</p>
                    <p className="popup-amount">
                        <Icon name="star" style={{ color: '#FFD700' }} />
                        {env.userTokenCount?.toLocaleString() || 0}
                    </p>
                    <p className="popup-messages">
                        ≈ {Math.floor((env.userTokenCount || 0) / 10)} {t('creditsWidget.messages')}
                    </p>
                    {isLow && (
                        <p className="popup-warning">
                            <Icon name="warning sign" />
                            {t('creditsWidget.lowWarning')}
                        </p>
                    )}
                    <p className="popup-cta">
                        <Icon name="shopping cart" />
                        {t('GetNewCredits')}
                    </p>
                </div>
            }
            position="bottom center"
            hoverable
            style={{ 
                backgroundColor: '#002925', 
                color: '#ffffff',
                border: '1px solid #003d3a'
            }}
        />
    );
};

export default CreditsWidget;
